import { Controller } from "@hotwired/stimulus"
import SlimSelect from 'slim-select'

export default class extends Controller {
  static targets = ["dataSource", "object", "field", "fieldWrapper"]
  static values = {
    accountId: String,
    dataSourceId: String
  }

  connect() {
    // Initialize debounce timers
    this.loadObjectsDebounceTimer = null
    this.loadFieldsDebounceTimer = null

    // Get initial values from data attributes
    this.initialObjectValue = this.hasObjectTarget ?
      (this.objectTarget.dataset.salesforceObjectFieldSelected || this.objectTarget.value) : null
    this.initialFieldValue = this.hasFieldTarget ?
      (this.fieldTarget.dataset.salesforceObjectFieldSelected || this.fieldTarget.value) : null

    // Initialize SlimSelect instances
    this.initializeSelects()

    // Only load objects if we have a data source ID
    // (either from initial value or from selected dropdown)
    if (this.dataSourceIdValue && this.dataSourceIdValue !== '') {
      this.loadObjects()
    }
  }

  disconnect() {
    // Clean up SlimSelect instances
    if (this.dataSourceSlimSelect) {
      this.dataSourceSlimSelect.destroy()
    }
    if (this.objectSlimSelect) {
      this.objectSlimSelect.destroy()
    }
    if (this.fieldSlimSelect) {
      this.fieldSlimSelect.destroy()
    }
  }

  initializeSelects() {
    // Initialize SlimSelect for data source dropdown if present
    if (this.hasDataSourceTarget) {
      this.dataSourceSlimSelect = new SlimSelect({
        select: this.dataSourceTarget,
        settings: {
          showSearch: true,
          searchText: 'No results',
          searchPlaceholder: 'Search data sources...',
          searchHighlight: true
        },
        events: {
          afterChange: () => {
            // Update data source and reload objects
            this.updateDataSource()
          }
        }
      })
    }

    // Initialize SlimSelect for object dropdown
    if (this.hasObjectTarget) {
      this.objectSlimSelect = new SlimSelect({
        select: this.objectTarget,
        settings: {
          showSearch: true,
          searchText: 'No results',
          searchPlaceholder: 'Search objects...',
          searchHighlight: true
        },
        events: {
          afterChange: () => {
            // Trigger field loading when object changes
            this.loadFields()
          }
        }
      })

      // Initially disable if no data source is selected AND no existing value
      const dataSourceValue = this.dataSourceIdValue || (this.hasDataSourceTarget ? this.dataSourceTarget.value : '')
      const hasExistingValue = this.objectTarget.value && this.objectTarget.value !== ''
      if ((!dataSourceValue || dataSourceValue === '') && !hasExistingValue) {
        this.objectSlimSelect.setData([{
          text: 'Select a data source first',
          value: '',
          placeholder: true
        }])
        this.objectSlimSelect.disable()
      }
    }

    // Initialize SlimSelect for field dropdown
    if (this.hasFieldTarget) {
      this.fieldSlimSelect = new SlimSelect({
        select: this.fieldTarget,
        settings: {
          showSearch: true,
          searchText: 'No results',
          searchPlaceholder: 'Search fields...',
          searchHighlight: true
        }
      })

      // Initially disable if no object is selected AND no existing value
      const objectValue = this.hasObjectTarget ? this.objectTarget.value : ''
      const hasExistingFieldValue = this.fieldTarget.value && this.fieldTarget.value !== ''
      if ((!objectValue || objectValue === '') && !hasExistingFieldValue) {
        this.fieldSlimSelect.setData([{
          text: 'Select an object first',
          value: '',
          placeholder: true
        }])
        this.fieldSlimSelect.disable()
      }
    }
  }

  updateDataSource() {
    // Get the selected data source ID
    if (this.dataSourceSlimSelect) {
      const selectedArray = this.dataSourceSlimSelect.getSelected()
      const selectedDataSourceId = Array.isArray(selectedArray) ? selectedArray[0] : selectedArray
      if (selectedDataSourceId && selectedDataSourceId !== '') {
        // Update the data source ID value
        this.dataSourceIdValue = selectedDataSourceId
        // Reload objects for the new data source
        this.loadObjects()
      } else {
        // Clear objects and fields if no data source selected
        this.clearObjectsAndFields()
      }
    }
  }

  clearObjectsAndFields() {
    if (this.objectSlimSelect) {
      this.objectSlimSelect.setData([{
        text: 'Select a data source first',
        value: '',
        placeholder: true
      }])
      this.objectSlimSelect.disable()
    }

    if (this.fieldSlimSelect) {
      this.fieldSlimSelect.setData([{
        text: 'Select an object first',
        value: '',
        placeholder: true
      }])
      this.fieldSlimSelect.disable()
    }
  }

  async loadObjects() {
    // Clear existing debounce timer
    if (this.loadObjectsDebounceTimer) {
      clearTimeout(this.loadObjectsDebounceTimer)
    }

    // Set new debounce timer
    this.loadObjectsDebounceTimer = setTimeout(() => {
      this.loadObjectsDebounced()
    }, 500)
  }

  async loadObjectsDebounced() {
    // Proceed with the actual API call
    if (!this.dataSourceIdValue || this.dataSourceIdValue === '') {
      console.log("No data source selected yet")
      // Set placeholder message instead of trying to load
      if (this.objectSlimSelect) {
        this.objectSlimSelect.setData([{
          text: 'Select a data source first',
          value: '',
          placeholder: true
        }])
        this.objectSlimSelect.disable()
      }
      return
    }

    if (!this.objectSlimSelect) {
      console.error("Object SlimSelect not initialized")
      return
    }

    const selectedArray = this.objectSlimSelect.getSelected()
    const currentValue = Array.isArray(selectedArray) ? selectedArray[0] : selectedArray

    // Enable the dropdown and show loading state
    this.objectSlimSelect.enable()
    this.objectSlimSelect.setData([{
      text: 'Loading objects...',
      value: '',
      placeholder: true
    }])

    try {
      const response = await fetch(`/admin/accounts/${this.accountIdValue}/data_sources/${this.dataSourceIdValue}/salesforce/objects`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      // Format data for SlimSelect
      const slimSelectData = [{
        text: 'Select an object...',
        value: '',
        placeholder: true
      }]

      data.objects.forEach(obj => {
        let label = `${obj.label} (${obj.name})`
        let disabled = false

        // If an object is not updateable, we show it
        // but disable it in the dropdown
        if (!obj.updateable) {
          label += ' (Read Only)'
          disabled = true
        }

        slimSelectData.push({
          text: label,
          value: obj.name,
          disabled: disabled
        })
      })

      // Update the dropdown with new data
      this.objectSlimSelect.setData(slimSelectData)
      this.objectSlimSelect.enable()

      // Restore the value - prefer current selection, then initial value
      const valueToRestore = currentValue || this.initialObjectValue
      if (valueToRestore && data.objects.some(obj => obj.name === valueToRestore)) {
        this.objectSlimSelect.setSelected(valueToRestore)
        // Load fields for the selected object
        this.loadFields()
      }

    } catch (error) {
      console.error("Error loading Salesforce objects:", error)
      this.objectSlimSelect.setData([{
        text: 'Error loading objects',
        value: '',
        placeholder: true
      }])
      this.objectSlimSelect.enable()
      this.showError("Failed to load Salesforce objects. Please check your Salesforce connection.")
    }
  }

  async loadFields() {
    // Clear existing debounce timer
    if (this.loadFieldsDebounceTimer) {
      clearTimeout(this.loadFieldsDebounceTimer)
    }

    // Set new debounce timer
    this.loadFieldsDebounceTimer = setTimeout(() => {
      this.loadFieldsDebounced()
    }, 500)
  }

  async loadFieldsDebounced() {
    // Proceed with the actual API call
    // Check if we have the required SlimSelect instances
    if (!this.fieldSlimSelect) {
      console.error("Field SlimSelect not initialized")
      return
    }

    // Check if we have a data source selected
    if (!this.dataSourceIdValue || this.dataSourceIdValue === '') {
      console.log("No data source selected yet")
      this.fieldSlimSelect.setData([{
        text: 'Select a data source first',
        value: '',
        placeholder: true
      }])
      this.fieldSlimSelect.disable()
      return
    }

    // Check if we have an object selected
    if (!this.objectSlimSelect) {
      console.error("Object SlimSelect not initialized")
      return
    }

    const selectedArray = this.objectSlimSelect.getSelected()
    const objectName = Array.isArray(selectedArray) ? selectedArray[0] : selectedArray
    if (!objectName || objectName === '') {
      console.log("No object selected yet")
      this.fieldSlimSelect.setData([{
        text: 'Select an object first',
        value: '',
        placeholder: true
      }])
      this.fieldSlimSelect.disable()
      return
    }

    const selectedFieldArray = this.fieldSlimSelect.getSelected()
    const currentFieldValue = Array.isArray(selectedFieldArray) ? selectedFieldArray[0] : selectedFieldArray

    // Show loading state
    this.fieldSlimSelect.disable()
    this.fieldSlimSelect.setData([{
      text: 'Loading fields...',
      value: '',
      placeholder: true
    }])

    try {
      const response = await fetch(`/admin/accounts/${this.accountIdValue}/data_sources/${this.dataSourceIdValue}/salesforce/fields?object_name=${encodeURIComponent(objectName)}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      // Format data for SlimSelect
      const slimSelectData = []

      if (data.fields.length === 0) {
        slimSelectData.push({
          text: 'No updatable numeric fields found',
          value: '',
          placeholder: true
        })
      } else {
        slimSelectData.push({
          text: 'Select a field...',
          value: '',
          placeholder: true
        })

        data.fields.forEach(field => {
          slimSelectData.push({
            text: `${field.label} (${field.name}) - ${field.type}`,
            value: field.name
          })
        })
      }

      // Update the dropdown with new data
      this.fieldSlimSelect.setData(slimSelectData)
      this.fieldSlimSelect.enable()

      // Restore the value - prefer current selection, then initial value
      const valueToRestore = currentFieldValue || this.initialFieldValue
      if (valueToRestore && data.fields.some(field => field.name === valueToRestore)) {
        this.fieldSlimSelect.setSelected(valueToRestore)
      }

    } catch (error) {
      console.error("Error loading Salesforce fields:", error)
      this.fieldSlimSelect.setData([{
        text: 'Error loading fields',
        value: '',
        placeholder: true
      }])
      this.fieldSlimSelect.enable()
      this.showError(`Failed to load fields for ${objectName}. Please check your Salesforce connection.`)
    }
  }

  showError(message) {
    // Create or update error message element
    let errorEl = this.element.querySelector(".salesforce-api-error")
    if (!errorEl) {
      errorEl = document.createElement("div")
      errorEl.className = "salesforce-api-error bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative mt-4"
      errorEl.innerHTML = `
        <span class="block sm:inline">${message}</span>
        <span class="absolute top-0 bottom-0 right-0 px-4 py-3">
          <svg class="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" onclick="this.parentElement.parentElement.remove()">
            <title>Close</title>
            <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
          </svg>
        </span>
      `
      // Insert after the title section
      const titleSection = this.element.querySelector('.md\\:col-span-1')
      if (titleSection && titleSection.parentElement) {
        titleSection.parentElement.insertBefore(errorEl, titleSection.nextSibling)
      } else {
        this.element.prepend(errorEl)
      }
    } else {
      errorEl.textContent = message
    }
  }

  clearError() {
    const errorEl = this.element.querySelector(".salesforce-api-error")
    if (errorEl) {
      errorEl.remove()
    }
  }
}